/**
 * TechniqueSelector (#3630) — techniques as index entries, third step of the
 * Gift funnel.
 *
 * Lists the technique options (pool ∪ signature) for the chosen gift, grouped
 * by category using the same Offense/Defense/Enhancement/Affliction/Utility
 * labels the old CantripSelector used for cantrip archetypes. Picks are capped
 * at `draft.starting_technique_picks` (base 1 + distinction bonus).
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TechniqueEffectSummaryDisplay } from '@/magic/components/TechniqueEffectSummary';
import { CodexLine, Entry, EntryDoors, EntryList } from '../../folio';
import {
  characterCreationKeys,
  useCGExplanations,
  useCGTechniqueOptions,
  usePersonalizationOptions,
  useUpdateDraft,
} from '../../queries';
import type { CGTechniqueOption, CharacterDraft, DraftData } from '../../types';
import { PersonalizationPanel } from './PersonalizationPanel';

const CATEGORY_LABELS: Record<CGTechniqueOption['category'], string> = {
  attack: 'Offense',
  defense: 'Defense',
  buff: 'Enhancement',
  debuff: 'Affliction',
  utility: 'Utility',
};

const CATEGORY_ORDER: CGTechniqueOption['category'][] = [
  'attack',
  'defense',
  'buff',
  'debuff',
  'utility',
];

interface TechniqueSelectorProps {
  draft: CharacterDraft;
  giftId: number;
}

/**
 * Toggling a technique: deselect it if it is already chosen, refuse to add when
 * the budget is spent, otherwise add it.
 */
function nextSelection(
  selectedIds: number[],
  techniqueId: number,
  isSelected: boolean,
  atBudget: boolean
): number[] {
  if (isSelected) return selectedIds.filter((id) => id !== techniqueId);
  if (atBudget) return selectedIds;
  return [...selectedIds, techniqueId];
}

export function TechniqueSelector({ draft, giftId }: TechniqueSelectorProps) {
  const updateDraft = useUpdateDraft();
  const queryClient = useQueryClient();
  const { data: options, isLoading } = useCGTechniqueOptions(
    draft.id,
    giftId,
    draft.selected_species?.id
  );
  const { data: personalizationOptions } = usePersonalizationOptions(draft);
  const { data: copy } = useCGExplanations();
  const selectedIds = draft.draft_data.selected_technique_ids ?? [];
  const picks = draft.starting_technique_picks;
  const traditionName = draft.selected_tradition?.name ?? 'Tradition';

  // Clear stale picks when the option set changes (gift swap, tradition swap, etc.)
  useEffect(() => {
    if (!options) return;
    const availableIds = new Set(options.map((t) => t.id));
    const filtered = selectedIds.filter((id) => availableIds.has(id));
    if (filtered.length !== selectedIds.length) {
      updateDraft.mutate({
        draftId: draft.id,
        data: {
          draft_data: {
            selected_technique_ids: filtered,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when the option set changes, not on every draft mutation
  }, [options]);

  if (isLoading) {
    return (
      <p className="ledger-line" aria-busy="true">
        Loading techniques…
      </p>
    );
  }

  if (!options || options.length === 0) {
    return <p className="ledger-line">No techniques are available for this gift.</p>;
  }

  const atBudget = selectedIds.length >= picks;

  const toggle = (techniqueId: number) => {
    const isSelected = selectedIds.includes(techniqueId);
    const next = nextSelection(selectedIds, techniqueId, isSelected, atBudget);
    if (next === selectedIds) return;
    const draftData: Partial<DraftData> = { selected_technique_ids: next };
    if (isSelected) {
      // Deselecting: drop this technique's "make it yours" picks in the same
      // write (plan fork 15) — a deselected technique needs no personalization UI.
      // Built from the freshest cached draft, not this render's `draft` prop
      // (#4099 final fix): PersonalizationPanel can write another technique's
      // pick into the cache after this component's last render, and building
      // from the stale prop would silently drop it from this write, the same
      // race PersonalizationPanel's own `write` already guards against.
      const cached = queryClient.getQueryData<CharacterDraft>(characterCreationKeys.draft());
      const latestPersonalizations = (cached ?? draft).draft_data.technique_personalizations ?? {};
      const { [String(techniqueId)]: _dropped, ...rest } = latestPersonalizations;
      draftData.technique_personalizations = rest;
    }
    updateDraft.mutate({
      draftId: draft.id,
      data: { draft_data: draftData },
    });
  };

  const personalizationFor = (techniqueId: number) =>
    personalizationOptions?.find((row) => row.technique_id === techniqueId);

  const grouped = CATEGORY_ORDER.map((category) => ({
    category,
    label: CATEGORY_LABELS[category],
    options: options.filter((technique) => technique.category === category),
  })).filter((group) => group.options.length > 0);

  return (
    <div>
      <p className="ledger-line" role="status">
        {selectedIds.length} of {picks} chosen
      </p>

      {grouped.map((group) => (
        <div key={group.category}>
          <h3 className="section-h">{group.label}</h3>
          <EntryList label={`${group.label} techniques`}>
            {group.options.map((technique) => {
              const isSelected = selectedIds.includes(technique.id);
              const closed = atBudget && !isSelected;
              let tag = 'Pool';
              if (closed) {
                tag = 'Budget reached';
              } else if (technique.is_species_technique) {
                tag = 'Species gift technique';
              } else if (technique.is_tradition_technique) {
                tag = `${traditionName} technique`;
              }
              return (
                <Entry
                  key={technique.id}
                  name={technique.name}
                  tag={tag}
                  chosen={isSelected}
                  closed={closed}
                  open={isSelected}
                  onChoose={() => toggle(technique.id)}
                  onSetAside={() => toggle(technique.id)}
                >
                  <p>{technique.description}</p>
                  <TechniqueEffectSummaryDisplay
                    summary={technique.effect_summary}
                    variant="full"
                  />
                  <CodexLine entryId={technique.codex_entry_id} name={technique.name} />
                  {isSelected && personalizationFor(technique.id) && (
                    <PersonalizationPanel
                      draft={draft}
                      options={personalizationFor(technique.id)!}
                      copy={copy}
                    />
                  )}
                  {/* At budget and not chosen: no doors at all, as Heritage
                      does for an unaffordable species. A "Choose" button that
                      silently refuses is worse than no button. */}
                  {!closed && (
                    <EntryDoors
                      chooseLabel={`Select ${technique.name}`}
                      onChoose={() => toggle(technique.id)}
                      chosen={isSelected}
                      onSetAside={() => toggle(technique.id)}
                    />
                  )}
                </Entry>
              );
            })}
          </EntryList>
        </div>
      ))}
    </div>
  );
}
